/**
 * MatterConnectedPane - the work hanging off this matter, in one place.
 *
 * FIVE groups, all from ONE call: `GET /matters/{uuid}/connected`, fetched by
 * `useConnectedWork` (which documents what it replaced and why). This pane is
 * a renderer plus one action - no fetching, no stitching, no filtering.
 *
 *   Research   opens `/research/{chat_group_uuid}`
 *   Drafts     opens `/drafting/drafts/{document_uuid}`
 *   Insights   opens `/drafting/insights/{uuid}`
 *   Reviews    opens `/reviews/{uuid}`
 *   Tables     opens `/tables/{uuid}`
 *
 * Reviews and tables are new here. They were always LINKABLE - a review or a
 * table started from a matter carries `matter_id` - but nothing in the matter
 * ever listed them, so half the tool suite was attached and invisible. The
 * pane now shows the whole suite, and "Connect" opens the picker
 * (`ConnectToMatterPanel`) so the link can also be made from this side.
 *
 * A group with nothing in it renders its heading and says so, rather than
 * disappearing: "no drafts yet" is information, and a group that vanishes
 * reads as a feature that is missing.
 *
 * SPACING: rows are `Well`s at the standard inner padding, groups are spaced a
 * full step apart. The rows used to be `px-2 py-1.5` boxes stacked at 6px,
 * which put a title's descenders against the border and made three lists read
 * as one dense block.
 */

import React from 'react';
import { useTranslation } from 'react-i18next';
import { useHistory } from 'react-router-dom';
import {
  PiArrowSquareOutLight,
  PiFileMagnifyingGlassLight,
  PiFileTextLight,
  PiLightbulbLight,
  PiLinkLight,
  PiListMagnifyingGlassLight,
  PiTableLight,
} from 'react-icons/pi';
import { useAppDispatch } from '../../../store';
import { showSidePanel } from '../../../store/actions/sidePanelActions';
import { formatDateTime } from '../../../utils/functions';
import { ConnectableType, ConnectedWork, EMPTY_CONNECTED } from './useConnectedWork';
import Skeleton from '../../shared/TailwindComponents/Skeleton';
import Badge from '../../shared/TailwindComponents/Badge';
import type { BadgeColor } from '../../shared/TailwindComponents/types/badgeTypes';

interface Props {
  matterUuid: string;
  /**
   * Fetched by the owner, not here - the rail badge needs the counts while
   * this pane is closed, and a pane that is not mounted cannot fetch.
   */
  work?: ConnectedWork;
  loading?: boolean;
  /** Re-run the owner's fetch after something is connected or disconnected. */
  onRefresh?: () => void;
}

const STATUS_BADGE: Record<string, BadgeColor> = {
  queued: 'blue',
  processing: 'yellow',
  completed: 'green',
  failed: 'red',
};

const Group: React.FC<{
  icon: React.ReactNode;
  title: string;
  count: number;
  /** "Connect" - opens the picker on this group's type. */
  onConnect?: () => void;
  connectLabel?: string;
  children: React.ReactNode;
}> = ({ icon, title, count, onConnect, connectLabel, children }) => (
  <div>
    <div className="flex items-center gap-1.5 mb-2 text-fg-muted">
      <span className="[&>svg]:h-3.5 [&>svg]:w-3.5">{icon}</span>
      <h4 className="text-[10px] font-semibold uppercase tracking-wider">{title}</h4>
      {count > 0 && <span className="text-[10px] tabular-nums">{count}</span>}
      {onConnect && (
        <button
          type="button"
          onClick={onConnect}
          className="ms-auto flex items-center gap-1 text-[10px] text-fg-muted
                     hover:text-champagne transition-colors duration-200 ease-slide"
        >
          <PiLinkLight className="h-3 w-3" />
          {connectLabel}
        </button>
      )}
    </div>
    {children}
  </div>
);

const Empty: React.FC<{ children: React.ReactNode }> = ({ children }) => (
  <p className="text-xs text-fg-muted leading-relaxed">{children}</p>
);

const LinkRow: React.FC<{
  title: string;
  meta?: string;
  badge?: React.ReactNode;
  onOpen?: () => void;
}> = ({ title, meta, badge, onOpen }) => {
  const interactive = !!onOpen;
  const Wrapper: any = interactive ? 'button' : 'div';
  return (
    <Wrapper
      {...(interactive ? { type: 'button', onClick: onOpen } : {})}
      className={
        'group w-full text-start rounded-md px-3 py-2.5 bg-surface-sunken/70 ' +
        'border border-hairline-soft transition-colors duration-200 ease-slide ' +
        (interactive ? 'hover:border-champagne/60' : '')
      }
    >
      <div className="flex items-center justify-between gap-2">
        <span className="text-xs font-medium text-fg-body truncate">{title}</span>
        <span className="flex items-center gap-1.5 flex-shrink-0">
          {badge}
          {interactive && (
            <PiArrowSquareOutLight
              className="h-3.5 w-3.5 text-fg-muted opacity-0 group-hover:opacity-100 transition-opacity"
            />
          )}
        </span>
      </div>
      {meta && <span className="mt-1 block text-[10px] text-fg-muted">{meta}</span>}
    </Wrapper>
  );
};

const MatterConnectedPane: React.FC<Props> = ({
  matterUuid,
  work = EMPTY_CONNECTED,
  loading = false,
  onRefresh,
}) => {
  const { t } = useTranslation();
  const history = useHistory();
  const dispatch = useAppDispatch();

  const {
    research_sessions: sessions,
    drafts,
    insights,
    reviews,
    tables,
    counts,
  } = work;

  const statusBadge = (status?: string) => {
    if (!status) return undefined;
    return (
      <Badge
        color={STATUS_BADGE[status] || 'gray'}
        className="!text-[10px] !px-1.5 !py-0 capitalize"
      >
        {t(status.replace(/_/g, ' '))}
      </Badge>
    );
  };

  const when = (row: { updated_at?: string }) =>
    row.updated_at ? formatDateTime(row.updated_at) : undefined;

  /** One picker, opened on the group the user pressed. */
  const openPicker = (initialType: ConnectableType) => () => {
    dispatch(showSidePanel({
      type: 'connectToMatter',
      showSidePanel: true,
      size: 'md',
      message: t('Connect to this matter'),
      subMsg: t('Attach research, drafts, insights, reviews and tables to this matter.'),
      icon: <PiLinkLight className="w-5 h-5" />,
      matterUuid,
      initialType,
      onChanged: onRefresh,
    } as any));
  };

  const canConnect = !!matterUuid;
  const connect = (type: ConnectableType) => (canConnect ? openPicker(type) : undefined);

  return (
    <div className="p-3 space-y-5">
      <Group
        icon={<PiListMagnifyingGlassLight />}
        title={t('Research')}
        count={counts.research_sessions}
        onConnect={connect('research_session')}
        connectLabel={t('Connect')}
      >
        {loading ? (
          <Skeleton variant="list" rows={2} />
        ) : sessions.length === 0 ? (
          <Empty>{t('No research sessions are linked to this matter yet.')}</Empty>
        ) : (
          <div className="space-y-2">
            {sessions.map((session) => {
              /* The research page is addressed by CHAT GROUP, not by session.
                 `uuid` is the fallback only because the two coincide for
                 sessions created before the split. */
              const target = session.chat_group_uuid || session.uuid;
              return (
                <LinkRow
                  key={session.uuid}
                  title={session.title || t('Untitled session')}
                  meta={when(session) || (session.last_message_at
                    ? formatDateTime(session.last_message_at)
                    : undefined)}
                  onOpen={target ? () => history.push(`/research/${target}`) : undefined}
                />
              );
            })}
          </div>
        )}
      </Group>

      <Group
        icon={<PiFileTextLight />}
        title={t('Drafts')}
        count={counts.drafts}
        onConnect={connect('draft')}
        connectLabel={t('Connect')}
      >
        {loading ? (
          <Skeleton variant="list" rows={2} />
        ) : drafts.length === 0 ? (
          <Empty>{t('No drafts have been generated for this matter yet.')}</Empty>
        ) : (
          <div className="space-y-2">
            {drafts.map((draft) => {
              /* Drafts DO have a home now - the drafting page addresses the
                 DOCUMENT, which is why `document_uuid` is what opens it. A
                 row without one is listed but inert rather than linking
                 somewhere that 404s. */
              const target = draft.document_uuid;
              return (
                <LinkRow
                  key={draft.uuid || String(draft.document_uuid)}
                  title={draft.title || t('Untitled draft')}
                  meta={[draft.category, when(draft)].filter(Boolean).join(' · ') || undefined}
                  badge={statusBadge(draft.status)}
                  onOpen={target ? () => history.push(`/drafting/drafts/${target}`) : undefined}
                />
              );
            })}
          </div>
        )}
      </Group>

      <Group
        icon={<PiLightbulbLight />}
        title={t('Insights')}
        count={counts.insights}
        onConnect={connect('insight')}
        connectLabel={t('Connect')}
      >
        {loading ? (
          <Skeleton variant="list" rows={2} />
        ) : insights.length === 0 ? (
          <Empty>{t('No insights have been generated for this matter yet.')}</Empty>
        ) : (
          <div className="space-y-2">
            {insights.map((insight) => (
              <LinkRow
                key={insight.uuid}
                title={insight.title || t('Untitled insight')}
                meta={when(insight)}
                badge={statusBadge(insight.status)}
                onOpen={insight.uuid
                  ? () => history.push(`/drafting/insights/${insight.uuid}`)
                  : undefined}
              />
            ))}
          </div>
        )}
      </Group>

      <Group
        icon={<PiFileMagnifyingGlassLight />}
        title={t('Reviews')}
        count={counts.reviews}
        onConnect={connect('review')}
        connectLabel={t('Connect')}
      >
        {loading ? (
          <Skeleton variant="list" rows={2} />
        ) : reviews.length === 0 ? (
          <Empty>{t('No review sessions are linked to this matter yet.')}</Empty>
        ) : (
          <div className="space-y-2">
            {reviews.map((review) => {
              const target = review.review_uuid || review.uuid;
              /* Findings, not a timestamp: what a review is worth knowing at a
                 glance is how much it found and how much of that is critical. */
              const facts = [
                review.document_count
                  ? t('{{count}} documents', { count: review.document_count })
                  : null,
                review.findings_count
                  ? t('{{count}} findings', { count: review.findings_count })
                  : null,
                review.critical_count
                  ? t('{{count}} critical', { count: review.critical_count })
                  : null,
                when(review),
              ].filter(Boolean);
              return (
                <LinkRow
                  key={review.uuid}
                  title={review.title || t('Untitled review')}
                  meta={facts.join(' · ') || undefined}
                  badge={statusBadge(review.status)}
                  onOpen={target ? () => history.push(`/reviews/${target}`) : undefined}
                />
              );
            })}
          </div>
        )}
      </Group>

      <Group
        icon={<PiTableLight />}
        title={t('Tables')}
        count={counts.tables}
        onConnect={connect('table')}
        connectLabel={t('Connect')}
      >
        {loading ? (
          <Skeleton variant="list" rows={2} />
        ) : tables.length === 0 ? (
          <Empty>{t('No tables are linked to this matter yet.')}</Empty>
        ) : (
          <div className="space-y-2">
            {tables.map((table) => {
              const target = table.table_uuid || table.uuid;
              const facts = [
                table.document_count
                  ? t('{{count}} documents', { count: table.document_count })
                  : null,
                table.column_count
                  ? t('{{count}} columns', { count: table.column_count })
                  : null,
                when(table),
              ].filter(Boolean);
              return (
                <LinkRow
                  key={table.uuid}
                  title={table.title || t('Untitled table')}
                  meta={facts.join(' · ') || undefined}
                  badge={statusBadge(table.status)}
                  onOpen={target ? () => history.push(`/tables/${target}`) : undefined}
                />
              );
            })}
          </div>
        )}
      </Group>
    </div>
  );
};

export default MatterConnectedPane;
